import React, { useContext, useEffect, useState } from 'react';
import './SurfaceTractPage.scss';
import { useForm } from 'react-hook-form';
import Button from 'src/components/controls/forms/redesign/FormButton';
import SurfaceTract from 'src/models/SurfaceTract';
import useFetch from 'use-http';
import SurfaceTractCardRHF from './tract/components/SurfaceTractCard.redesign';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { Url } from 'src/api';
import Project from 'src/models/Project';
import moment from 'moment';
import OwnershipContactCategoryForm from 'src/models/OwnershipContactCategoryForm';
import { Tag } from 'react-tag-autocomplete';
import GlobalOwnershipList from './TractComponents/OwnershipList.redesign';
import Ownership from 'src/models/tract_detail/Ownership';
import MineralTractList from './tract/components/MineralTractList';
import TractDetail from 'src/models/tract_detail/TractDetail';
import Horizon from 'src/models/tract_detail/Horizon';
import { SelectItem } from 'src/models/common/SelectItem';
import { AnyObject } from 'final-form';
import Abstract from 'src/models/Abstract';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import PagedDropdown from 'src/components/controls/PagedDropdown';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { LandSystem } from 'src/models/LandSystem';
import AgreementUtil from 'src/util/AgreementUtil';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import County from 'src/models/County';
import { dialogCreators } from 'src/providers/dialogs';
import { DialogContext } from 'src/providers/DialogProvider';
import OwnershipCategory from 'src/models/OwnershipCategory';

const SurfaceTractPage = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const surfaceOwnershipsRef = React.createRef();
    const dispatch = useDispatch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const { getValues, setValue, handleSubmit, formState, register, errors, reset } = useForm();
    const { get, put, response } = useFetch();
    const [surfaceTract, setSurfaceTract] = useState<SurfaceTract>(undefined);
    const [projectData, setProjectData] = useState<Project>(undefined);
    const [counties, setCounties] = useState<County[]>([]);
    const [ownershipContactCategories, setOwnershipContactCategories] = useState<OwnershipContactCategoryForm>(undefined);
    const [surfaceTags, setSurfaceTags] = useState<Tag[]>([]);
    const [surfaceAbstracts, setSurfaceAbstracts] = useState<Abstract[]>([]);
    const [fieldsUpdated, setFieldsUpdated] = useState<boolean>(false);
    const [grossAcres, setGrossAcres] = useState<number>(0);
    const [leaseBuyerList, setLeaseBuyerList] = useState<Array<SelectItem>>([]);
    const [isActive, setIsActive] = useState<boolean>(false);
    // set to non-form-driven state to prevent lag and race conditions
    const [prelimSurfaceTract, setPrelimSurfaceTract] = useState<SurfaceTract>(undefined);

    useEffect(() => {
        getProjectData();
        getOwnershipContactCategories();
        getProjectStaff();
    }, []);

    useEffect(() => {
        if (appContext.surfaceTractNumber > 0) {
            setFieldsUpdated(false);
            loadSurfaceTract();
        }
    }, [appContext.surfaceTractNumber]);

    useEffect(() => {
        if (!!surfaceTract) {
            reset(surfaceTract);
            setGrossAcres(surfaceTract?.acres);
            setIsActive(surfaceTract.isActive);
        }
    }, [surfaceTract]);

    useEffect(() => {
        if (!!projectData) {
            getCounties(projectData.landSystemId);
        }
    }, [projectData]);

    useEffect(() => {
        if (!!prelimSurfaceTract && !! projectData) {
            let formatted = {
                ...prelimSurfaceTract, 
                ownerships: prepOwnerships(prelimSurfaceTract.ownerships)
            };
    
            setSurfaceTract(formatted);
        }
    }, [prelimSurfaceTract, projectData]);

    const updateGrossAcres = () => {
        let ref = getValues();
        if (ref.acres) {
            setGrossAcres(ref.grossAcres);
        }
    };
    
    const getCounties = async (landSystem: LandSystem) => {
        const countiesData = landSystem === LandSystem.EastTexas
            ? await get(Url.project.getProjectCounties(appContext))
            : await get(Url.project.getFieldCounties(appContext, appContext.fieldNumber));

        if (response.ok && countiesData?.items) {
            setCounties(countiesData.items);
        }
    };

    /*
     * Format dates for the date inputs without poisoning the values we hold in state.
     * moment(null) formats as the literal string 'Invalid date', and a default
     * DateTimeOffset ('0001-01-01T00:00:00+00:00') formats to '0000-12-31' in a negative
     * UTC offset. Neither string can be bound back to a DateTime/DateTimeOffset on save,
     * which fails the whole request body. Leave anything we can't format alone.
     */
    const isBindableDate = (value: Date) => {
        const parsed = moment(value);

        return !!value && parsed.isValid() && parsed.year() > 1;
    };

    const formatDateForInput = (value: Date) => {
        return isBindableDate(value) ? (moment(value).format('YYYY-MM-DD') as unknown as Date) : value;
    };

    const loadSurfaceTract = async () => {
        const result: SurfaceTract = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/surface-tracts/${appContext.surfaceTractNumber}`
        );

        if (response.ok) {
            if (result.recordTypeId) {
                result.recordDate = formatDateForInput(result.recordDate);
            }
            if (result.examinedHistories?.length > 0) {
                result.examinedHistories.forEach((eh) => {
                    eh.takeOffDate = formatDateForInput(eh.takeOffDate);
                    eh.examinedDate = formatDateForInput(eh.examinedDate);
                });
            }
            setPrelimSurfaceTract(result);
            setSurfaceTags(result.tags ?? []);
            setSurfaceAbstracts(result.abstracts ?? []);
        }
    };

    const getProjectData = async () => {
        const data = await get(Url.project.getProject(appContext, appContext.projectNumber));

        if (response.ok) {
            setProjectData(data);
        }
    };

    const getOwnershipContactCategories = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownershipcontactcategories`);

        if (response.ok) {
            setOwnershipContactCategories(result);
        }
    };

    const getProjectStaff = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/staffing`);

        if (response.ok) {
            if (!!result) {
                setLeaseBuyerList(result && result.items.length > 0
                    ? result.items
                          .map((landman: AnyObject) => new SelectItem(landman.id, landman.user.firstName + ' ' + landman.user.lastName))
                    : []
                );
            }    
        }
    };

    const updateSurfaceTags = (tags: Tag[]) => {
        setSurfaceTags([...tags]);
        setFieldsUpdated(true);
    };

    const updateSurfaceAbstracts = (abstracts: Abstract[]) => {
        setSurfaceAbstracts([...abstracts]);
        setFieldsUpdated(true);
    };

    const onSubmit = async (data: SurfaceTract) => {
        if (surfaceOwnershipsRef.current) {
            (surfaceOwnershipsRef.current as { saveForm: () => void }).saveForm();
        }

        data = formatSurfaceTract(data);
        data.ownerships = formatOwnerships(data.ownerships);
        data.mineralTracts = formatMineralTracts(data.mineralTracts);
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/surface-tracts`,
            data
        );

        if (response.ok) {
            setFieldsUpdated(false);
            loadSurfaceTract();
        }
    };

    /*
     * The notes dialog is seeded with the loaded surface tract, so submitting it back would
     * send the whole read payload - read-only collections and values formatted for display -
     * which the save endpoint can't bind. Send the page's own form values with the note
     * applied instead. The notes button is disabled while the form is dirty, so these match
     * what is already saved.
     */
    const saveSurfaceTractNote = (note: string) => {
        onSubmit({ ...getValues(), note } as unknown as SurfaceTract);
    };

    const cancelFormChanges = () => {
        window.location.reload();
    };

    const prepOwnerships = (ownerships: Array<Ownership>) => {
        for (let o of ownerships) {
            let categories = o.ownershipCategories ?? [];

            let oids = categories.map(oc => oc.categoryTypeId);
            let missing = projectData.ownershipCategoryTypes
                ?.filter(oct => !oids.includes(oct.id))
                ?.map(oct => {
                    let newCat: OwnershipCategory = {
                        categoryTypeId: oct.id,
                        categoryTypeName: oct.name,
                        ownershipId: o.id
                    };

                    return newCat;
                }
            );

            if (missing?.length > 0) {
                categories = categories.concat(missing);
            }

            categories.sort((a, b) => {
                return a.categoryTypeName?.localeCompare(b.categoryTypeName) ?? 0;
            });
            
            o.ownershipCategories = categories;

            if (o.additionalOwnerships?.length > 0) {
                o.additionalOwnerships = prepOwnerships(o.additionalOwnerships);
            }
        }

        return ownerships;
    };

    const formatSurfaceTract = (st: SurfaceTract) => {
        st.id = surfaceTract ? surfaceTract.id : 0;
        st.fieldId = surfaceTract ? surfaceTract.fieldId : 0;
        st.number = surfaceTract ? surfaceTract.number : 0;
        st.createdByUserId = surfaceTract ? surfaceTract.createdByUserId : null;
        st.modifiedByUserId = surfaceTract ? surfaceTract.modifiedByUserId : null;
        st.recordTypeId = st.recordTypeId ? parseInt(st.recordTypeId + '', 10) : null;
        st.instrumentTypeId = st.instrumentTypeId ? parseInt(st.instrumentTypeId + '', 10) : null;
        st.countyId = st.countyId ? parseInt(st.countyId + '', 10) : null;

        st.acres = st.acres ? parseFloat(st.acres + '') : null;
        st.partialAcreage = st.partialAcreage ? parseFloat(st.partialAcreage + '') : null;
        st.rowLength = st.rowLength ? parseFloat(st.rowLength + '') : null;
        
        st.isActive = AgreementUtil.parseBool(st.isActive + '');
        st.isPartial = AgreementUtil.parseBool(st.isPartial + '') ?? false;
        
        st.name = st.name ? st.name : '';
        st.grantee = st.grantee ? st.grantee : '';
        st.grantor = st.grantor ? st.grantor : '';
        st.book = st.book ? st.book : '';
        st.page = st.page ? st.page : '';
        st.boundByNorth = st.boundByNorth ? st.boundByNorth : '';
        st.boundByEast = st.boundByEast ? st.boundByEast : '';
        st.boundBySouth = st.boundBySouth ? st.boundBySouth : '';
        st.boundByWest = st.boundByWest ? st.boundByWest : '';
        st.legalDescription = st.legalDescription ? st.legalDescription : '';

        st.recordDate = isBindableDate(st.recordDate) ? formatDateForInput(st.recordDate) : null;

        /* 'note' isn't a field on this form - keep whatever is already on the tract. */
        st.note = st.note !== undefined ? st.note : surfaceTract?.note;

        st.tags = [...surfaceTags];
        st.abstracts = [...surfaceAbstracts];

        return st;
    };

    const formatOwnerships = (so: Ownership[]) => {
        if (so?.length > 0) {
            so = so.map((ownership: Ownership) => {
                ownership.id = ownership.id ? parseInt(ownership.id + '', 10) : 0;
                ownership.parentOwnershipId = ownership.parentOwnershipId ? parseInt(ownership.parentOwnershipId + '', 10) : 0;
                ownership.additionalOwnershipTypeId = ownership.additionalOwnershipTypeId
                    ? parseInt(ownership.additionalOwnershipTypeId + '', 10)
                    : 0;
                ownership.ownershipContactCategoryId = ownership.ownershipContactCategoryId
                    ? parseInt(ownership.ownershipContactCategoryId + '', 10)
                    : null;
                ownership.leaseBuyer = ownership.leaseBuyer
                    ? parseInt(ownership.leaseBuyer + '', 10)
                    : null;
                    
                /* DecimalInterest is a non-nullable decimal on the server - null fails to bind. */
                ownership.decimalInterest = ownership.decimalInterest ? parseFloat(ownership.decimalInterest + '') : 0;
                ownership.executiveRights = ownership.executiveRights ? parseFloat(ownership.executiveRights + '') : null;

                if (ownership.ownershipCategories?.length > 0) {
                    for (let cat of ownership.ownershipCategories) {
                        cat.id = cat.id ? parseInt(cat.id + '', 10) : 0;
                        cat.ownershipId = ownership.id;
                        cat.categoryTypeId = cat.categoryTypeId ? parseInt(cat.categoryTypeId + '', 10) : null;
                        cat.categorySelectionId = cat.categorySelectionId ? parseInt(cat.categorySelectionId + '', 10) : null;
                    }
                }
                if (ownership.additionalOwnerships?.length > 0) {
                    ownership.additionalOwnerships = formatOwnerships(ownership.additionalOwnerships);
                }

                return ownership;
            });
        }
        return so;
    };

    const formatMineralTracts = (mt: TractDetail[]) => {
        if (mt?.length > 0) {
            mt = mt.map((tract: TractDetail) => {
                tract.id = tract.id ? parseInt(tract.id + '', 10) : 0;
                tract.tractNumber = tract.tractNumber ? parseInt(tract.tractNumber + '', 10) : 0;
                tract.grossAcres = tract.grossAcres ? parseFloat(tract.grossAcres + '') : 0;
                tract.tractName = tract.tractName ? tract.tractName : '';
                tract.horizons = tract.horizons?.map((horizon: Horizon) => {
                    horizon.id = horizon.id ? parseInt(horizon.id + '', 10) : 0;
                    horizon.partialAcreage = horizon.partialAcreage ? parseFloat(horizon.partialAcreage + '') : 0;

                    horizon.grantee = horizon.grantee ? horizon.grantee : '';
                    horizon.grantor = horizon.grantor ? horizon.grantor : '';
                    horizon.legalDescription = horizon.legalDescription ? horizon.legalDescription : '';

                    horizon.isPartial = horizon.isPartial ? horizon.isPartial : null;

                    if (horizon.mineralOwnerships?.length > 0) {
                        horizon.mineralOwnerships = formatOwnerships(horizon.mineralOwnerships);
                    }
                    return horizon;
                });

                return tract;
            });
        }
        return mt;
    };

    const surfaceTractItems = () => {
        if (!surfaceTract?.fieldTractNumbers) {
            return undefined;
        }

        return surfaceTract.fieldTractNumbers.map(
            (st: number, index: number) =>
                new SelectItem(st, `Tract ${st} (${index + 1} of ${surfaceTract.fieldTractNumbers.length})`)
        );
    };

    const changeSurfaceTractHandler = (item: SelectItem) => {
        const confirmAction = () => {
            toggleSurfaceTract(item.id as number);
        };

        if (formState.isDirty || fieldsUpdated) {
            const body = (
                <>
                    <div style={{ fontWeight: 'bold' }}>
                        You will lose unsaved changes. Continue?
                    </div>
                </>
            );
    
            dialogContext.common.confirmAction(`Unsaved Changes`, body, confirmAction);
        } else {
            confirmAction();
        }
    };

    const toggleSurfaceTract = (tractNumber: number) => {
        dispatch(
            push(
                `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${tractNumber}`
            )
        );
    };

    const handleActiveChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsActive(e.currentTarget.value === 'true');
    };

    return (
        <div className="tract-detail">
            <br />

            {isActive && (
                <div style={{backgroundColor: '#63c191', textAlign: 'center'}}>
                    <h2>Active</h2>
                </div>
            )}

            <div className="tract-nav">
                {appContext.sectionNumber && (
                    <h2 style={{ marginRight: '.7rem', fontWeight: 'lighter', marginLeft: '2rem' }}>
                        {`Global ID: ${appContext.clientNumber}.${appContext.projectNumber}.${appContext.sectionNumber}.${appContext.surfaceTractNumber}`}
                    </h2>
                )}
                {/* Deleting mapNumber reference. Project Routes only considers sectionNumber (even for Eastern states projects)  */}
            </div>

            <div className="tract-info-form">
                {surfaceTract && projectData && (
                    <form onSubmit={handleSubmit((data) => onSubmit(data as unknown as SurfaceTract))}>
                        {(formState.isDirty || fieldsUpdated) && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(191, 195, 203, .7)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        borderTop: '10px solid rgba(66, 184, 241, 1)',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: '.5rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            display: 'flex',
                                        }}
                                    >
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" disabled={formState.isSubmitting} />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button
                                            text="Cancel Changes"
                                            size="small"
                                            disabled={!formState.isDirty || formState.isSubmitting}
                                            onClick={cancelFormChanges}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="clearfix tract-data">
                            <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0', marginBottom: '.2rem' }}>
                                <legend className="legend">Surface Tract Information</legend>
                            </fieldset>

                            <RHFLegend title={`Tract Count: ${surfaceTract?.fieldTractNumbers?.length ?? 0}`} />

                            <FormRow>
                                <PagedDropdown
                                    value={appContext.surfaceTractNumber ?? 0}
                                    size="small"
                                    items={surfaceTractItems()}
                                    onChange={changeSurfaceTractHandler}
                                />
                            </FormRow>

                            <RHFToggle 
                                name="isActive" 
                                label="Active?" 
                                bodyStyle={{ width: '10%' }} 
                                register={register} 
                                errors={errors}
                                onChange={(e) => handleActiveChange(e)}
                            />

                            <div style={{ marginBottom: '1.5rem' }}>
                                <SurfaceTractCardRHF
                                    surfaceTract={surfaceTract}
                                    landSystem={projectData?.landSystemId}
                                    counties={counties}
                                    projectTags={projectData?.tags}
                                    surfaceTags={surfaceTags}
                                    surfaceAbstracts={surfaceAbstracts}
                                    hasSurfaceOwnerships={surfaceTract?.ownerships?.length > 0}
                                    updateSurfaceTags={(tags: Tag[]) => updateSurfaceTags(tags)}
                                    updateSurfaceAbstracts={(abstracts: Abstract[]) => updateSurfaceAbstracts(abstracts)}
                                    register={register}
                                    errors={errors}
                                    getValues={getValues}
                                    setValue={setValue}
                                    formState={formState}
                                    onChanges={() => loadSurfaceTract()}
                                    updateGrossAcres={() => updateGrossAcres()}
                                    saveSurfaceTractNote={(updatedTract: SurfaceTract) => saveSurfaceTractNote(updatedTract?.note)}
                                />
                            </div>
                        </div>

                        <div className="ownership-breakdown">
                            <GlobalOwnershipList
                                tractId={surfaceTract?.id}
                                namePrefix={'ownerships'}
                                ownershipContactCategories={ownershipContactCategories?.categories}
                                categoryTypes={projectData?.ownershipCategoryTypes}
                                projectType={'Surface'}
                                ownershipType={'Surface'}
                                ownerships={surfaceTract?.ownerships}
                                owners={surfaceTract?.owners}
                                grossAcres={grossAcres}
                                leaseBuyerList={leaseBuyerList}
                                loadSurfaceTract={() => loadSurfaceTract()}
                                register={register}
                                errors={errors}
                                getValues={getValues}
                                formState={formState}
                                reset={() => reset(surfaceTract)}
                            />
                        </div>
                        {projectData?.landSystemId !== LandSystem.RightOfWay && (
                            <div>
                                <MineralTractList
                                    landSystem={projectData?.landSystemId}
                                    ownershipContactCategories={ownershipContactCategories?.categories}
                                    categoryTypes={projectData?.ownershipCategoryTypes}
                                    mineralTracts={surfaceTract.mineralTracts}
                                    counties={projectData?.counties}
                                    leaseBuyerList={leaseBuyerList}
                                    loadSurfaceTract={() => loadSurfaceTract()}
                                    openContactNotes={function (ownership: Ownership): void {
                                        throw new Error('Function not implemented.');
                                    }}
                                    register={register}
                                    errors={errors}
                                    getValues={getValues}
                                    formState={formState}
                                    reset={() => reset(surfaceTract)}
                                />
                            </div>
                        )}

                        <div style={{ textAlign: 'right', margin: '1rem 1rem .7rem 0' }}>
                            <Button text="Save" type="submit" size="medium" intent="primary" disabled={formState.isSubmitting} />{' '}
                            <Button text="Cancel" size="medium" disabled={!formState.isDirty || formState.isSubmitting} onClick={cancelFormChanges} />
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SurfaceTractPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/SurfaceTractPage.tsx